import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationsProvider } from './context/NotificationsContext';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRole from './components/RequireRole';
import OfflineAuthGate from './components/OfflineAuthGate';
import ConnectionBanner from './components/ConnectionBanner';
import BetaBanner from './components/BetaBanner';
import UpdatePrompt from './components/UpdatePrompt';
import WhatsNewModal from './components/WhatsNewModal';
import { UpdatesProvider } from './components/content/UpdatesModal';
import Footer from './components/Footer';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import OpsBoard from './pages/OpsBoard';
import BetaConsole from './pages/BetaConsole';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import OrphanedBookings from './pages/OrphanedBookings';
import Enquiries from './pages/Enquiries';
import Tasks from './pages/Tasks';
import People from './pages/People';

export default function App() {
  return (
    <BrowserRouter>
      {/* Persistent beta marker — normal flow so it pushes content down (never covers it).
          Rendered above ConnectionBanner: the transient fixed red bar stacks over it when
          offline. Remove at GA. */}
      <BetaBanner />
      <ConnectionBanner />
      {/* PWA update banner (v0.3.1): owns SW registration and prompts to Refresh when a new
          build is waiting — no forced auto-reload (ops users may be mid-booking). */}
      <UpdatePrompt />
      <AuthProvider>
        {/* One shared notifications source for the whole app (header bell + board own-booking
            interrupt read the same instance — single poll, no double-fetch). */}
        <NotificationsProvider>
        {/* 5d Phase 4: the offline-auth gate stands in front of the routed app — it sends a
            freshly-authenticated unenrolled device through PIN enrolment, and a locked/expired
            enrolled device through PIN unlock, before the cached UI renders. Transparent when
            not authenticated (login flow) or already unlocked. */}
        <OfflineAuthGate>
        {/* Updates history (v2): owns the browsable past-notes panel. Wraps BOTH the popup
            and the routed app because its two entry points live on either side — the
            popup's "See past updates" link and the ☰ menu's "Updates" item. */}
        <UpdatesProvider>
        {/* What's-New popup (Phase 1): version-gated release notes, fires once per app
            version after login/unlock. Renders nothing unless authenticated with
            unseen published notes; best-effort online-only — never blocks a page.
            Dismissal is per USER since v2 (users.whatsnew_seen_version). */}
        <WhatsNewModal />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Staff-only board (security/student-read-lockdown). Same 7-role staff list the
              /enquiries route uses — NOT ROUTE_ACCESS_ROLES['/ops-board'] (it omits pilot, whom
              defaultHomeRoute() sends here). The backend GET /api/bookings 403 is the real gate. */}
          <Route
            path="/ops-board"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <OpsBoard />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/beta"
            element={
              <ProtectedRoute>
                <RequireRole role="admin">
                  <BetaConsole />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <RequireRole role="admin">
                  <Admin />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orphaned"
            element={
              <ProtectedRoute>
                <RequireRole role="admin">
                  <OrphanedBookings />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiries"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <Enquiries />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enquiries/:id"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <Enquiries />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          {/* Students & clients directory (PR-000016) — staff-only. Same STAFF_ROLE_NAMES set the
              backend GET /api/students/directory enforces (the API 403 is the real gate). */}
          <Route
            path="/people"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <People />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <Tasks />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute>
                <RequireRole anyOf={['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']}>
                  <Tasks />
                </RequireRole>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </UpdatesProvider>
        </OfflineAuthGate>
        </NotificationsProvider>
      </AuthProvider>
      {/* Site-wide version + copyright footer (§E) — sits at the bottom of every page's flow. */}
      <Footer />
    </BrowserRouter>
  );
}
